import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  ArrowLeft, 
  Download,
  ChevronRight,
  Loader2,
  Award,
  TrendingUp,
  Sparkles,
  Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { VerifiedActivityBadge, CapabilityEvidenceDrawer } from '../../components/provenance';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Domain colors matching the screenshot
const DOMAIN_COLORS = {
  stakeholder_communication: { bar: 'bg-red-500', badge: 'bg-red-500/20 text-red-400' },
  people_development: { bar: 'bg-cyan-500', badge: 'bg-cyan-500/20 text-cyan-400' },
  delivery_execution: { bar: 'bg-green-500', badge: 'bg-green-500/20 text-green-400' },
  personal_leadership: { bar: 'bg-yellow-500', badge: 'bg-yellow-500/20 text-yellow-400' },
  operational_excellence: { bar: 'bg-purple-500', badge: 'bg-purple-500/20 text-purple-400' },
  strategic_leadership: { bar: 'bg-blue-500', badge: 'bg-blue-500/20 text-blue-400' },
};

// Maturity level colors
const MATURITY_COLORS = {
  junior: 'bg-zinc-600 text-zinc-300',
  mid: 'bg-blue-600 text-blue-100',
  senior: 'bg-emerald-600 text-emerald-100',
  principal: 'bg-amber-600 text-amber-100',
};

const LivingCVPage = () => {
  const [cvData, setCvData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [evidenceDomain, setEvidenceDomain] = useState(null);

  useEffect(() => {
    const fetchLivingCV = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/entitlements/my-living-cv`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch Living CV');
        const data = await res.json();
        setCvData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchLivingCV();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center text-zinc-400">
          <p>Unable to load Living CV</p>
          <Link to="/dashboard" className="text-red-400 hover:underline mt-2 inline-block">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const { domains, total_evidence, maturity_levels } = cvData || {};
  const domainList = Object.entries(domains || {});
  const maxEvidence = Math.max(...domainList.map(([_, d]) => d.evidence_count), 10);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="living-cv-page">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link 
                to="/dashboard" 
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-zinc-400" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  Your Living CV
                </h1>
                <p className="text-xs text-zinc-500">
                  {total_evidence || 0} evidence points collected
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              disabled
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Intro Section */}
        <div className="mb-8">
          <p className="text-zinc-400 text-lg leading-relaxed mb-4">
            Every reflection builds evidence of your leadership capability. 
            Not a list of courses — <span className="text-white">a record of demonstrated behaviour.</span>
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
            <div className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-red-500" />
              <span>Structured against 6 leadership domains</span>
            </div>
            <div className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-red-500" />
              <span>Real evidence from real situations</span>
            </div>
            <div className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-red-500" />
              <span>Export-ready for reviews, promotions, interviews</span>
            </div>
          </div>
        </div>

        {/* PROV.1: Verified Activity Badge */}
        <div className="mb-6">
          <VerifiedActivityBadge />
        </div>

        {/* Domains List */}
        <div className="space-y-4" data-testid="domain-list">
          {domainList.length === 0 ? (
            <EmptyState />
          ) : (
            domainList.map(([domainId, domain]) => (
              <DomainCard
                key={domainId}
                domainId={domainId}
                domain={domain}
                maxEvidence={maxEvidence}
                isExpanded={expandedDomain === domainId}
                onToggle={() => setExpandedDomain(
                  expandedDomain === domainId ? null : domainId
                )}
                onViewEvidence={() => setEvidenceDomain(domainId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))}
              />
            ))
          )}
        </div>

        {/* Legend */}
        <div className="mt-8 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Maturity Levels
          </h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(maturity_levels || {}).map(([level, info]) => (
              <div key={level} className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${MATURITY_COLORS[level] || 'bg-zinc-700 text-zinc-300'}`}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </span>
                <span className="text-xs text-zinc-500">{info.evidence_range || ''}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* PROV.2: Capability Evidence Drawer */}
      <CapabilityEvidenceDrawer
        domain={evidenceDomain}
        isOpen={!!evidenceDomain}
        onClose={() => setEvidenceDomain(null)}
      />
    </div>
  );
};

// Domain Card Component
const DomainCard = ({ domainId, domain, maxEvidence, isExpanded, onToggle, onViewEvidence }) => {
  const colors = DOMAIN_COLORS[domainId] || { bar: 'bg-zinc-500', badge: 'bg-zinc-500/20 text-zinc-400' };
  const maturityColor = MATURITY_COLORS[domain.maturity_level] || 'bg-zinc-700 text-zinc-400';
  const barWidth = Math.max(5, (domain.evidence_count / maxEvidence) * 100);

  return (
    <div 
      className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors"
      data-testid={`domain-card-${domainId}`}
    >
      {/* Main row */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">{domain.short_name || domain.name}</h3>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-sm">
              {domain.evidence_count} evidence
            </span>
            {domain.maturity_level && (
              <span className={`text-xs px-2 py-0.5 rounded capitalize ${maturityColor}`}>
                {domain.maturity_level}
              </span>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-zinc-800 pt-3">
          <p className="text-zinc-500 text-sm mb-3">{domain.purpose}</p>
          
          {domain.recent_evidence?.length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                Recent Evidence
              </h4>
              {domain.recent_evidence.map((evidence, idx) => (
                <div 
                  key={idx}
                  className="p-3 bg-zinc-800/50 rounded-lg text-sm"
                >
                  <p className="text-zinc-300 mb-1">{evidence.evidence_text || evidence.description}</p>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    {evidence.sub_capability && (
                      <span className={`px-1.5 py-0.5 rounded ${colors.badge}`}>
                        {evidence.sub_capability}
                      </span>
                    )}
                    {evidence.extracted_at && (
                      <span>{new Date(evidence.extracted_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm italic">
              No evidence collected yet. Keep reflecting!
            </p>
          )}

          {/* PROV.2: View Evidence Trail button */}
          {onViewEvidence && domain.evidence_count > 0 && (
            <button
              data-testid={`view-evidence-${domainId}`}
              onClick={(e) => { e.stopPropagation(); onViewEvidence(); }}
              className="mt-3 flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5" />
              View verified evidence trail
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Empty State
const EmptyState = () => (
  <div className="text-center py-12">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-800 flex items-center justify-center">
      <Sparkles className="w-8 h-8 text-zinc-600" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">Start Building Your Living CV</h3>
    <p className="text-zinc-500 mb-6 max-w-md mx-auto">
      Your Living CV grows automatically as you reflect with the Companion. 
      Each reflection is analysed for leadership behaviours and added here.
    </p>
    <Link to="/companion">
      <Button className="bg-red-600 hover:bg-red-700 text-white">
        <TrendingUp className="w-4 h-4 mr-2" />
        Start a Reflection
      </Button>
    </Link>
  </div>
);

export default LivingCVPage;
